import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import MyProjects from "./MyProjects";
import { BASE_URL } from "utils/constant";
import Logo from "../../assets/site.png";
import SubscriptionModal from "./Subscription/SubscriptionModal";
import ModernB2BLoading from "components/UI_components/ModernLoading";
import {
  UserAPI,
  SubscriptionAPI,
  OrgAPI,
  SitesAPI,
} from "api/dashboard/service";
import { taskApi } from "api/tasks/taskApi";
import { LeadAPI } from "api/lead/service";
import { getDesignsByOrg, fetchOrganizationMembers } from "api/design/service";
import { FinanceApi } from "api/finance/service";
import { RiMoneyRupeeCircleFill } from "react-icons/ri";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Briefcase,
  FileText,
  CheckCircle,
  Clock,
  MoreHorizontal,
  ChevronRight,
  TrendingUp,
  DollarSign,
  Activity,
  ChevronDown,
  User,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { ProcurementApi } from "api/procurement/service";
import LoadingScreen from "components/UI_components/LoadingScreen";
// API functions
const apiClient = {
  fetchUserData: UserAPI.getUserDetails,

  fetchOrganizations: OrgAPI.listForUser,

  fetchProjectSites: async (organizationId, accessToken) => {
    const data = await SitesAPI.listByOrganization(organizationId);

    let sitesArr = [];
    if (Array.isArray(data)) {
      sitesArr = data;
    } else if (Array.isArray(data.data)) {
      sitesArr = data.data;
    }
    return sitesArr;
  },

  fetchSiteData: async (siteId, accessToken) => {
    const data = await SitesAPI.getById(siteId);
    if (!data) throw new Error("Failed to fetch site data");
    return data;
  },

  fetchOrgTransactions: async (organizationId, accessToken) => {
    const data = await OrgAPI.getTransactions(organizationId);
    return data;
  },

  fetchLeads: async (organizationId) => {
    const res = await LeadAPI.listLeads(organizationId);
    return res;
  },

  fetchDesignsByOrg: async (organizationId) => {
    const res = await getDesignsByOrg(organizationId);
    return res;
  },

  fetchOrgMembers: async (organizationId) => {
    const res = await fetchOrganizationMembers(organizationId);
    return res;
  },

  fetchFinanceByOrganization: async (organizationId, period = "30days") => {
    const res = await FinanceApi.listTransactionsByOrganization(organizationId, period);
    return res;
  },

  checkSubscription: async (organizationId, accessToken) => {
    const data = await SubscriptionAPI.checkUserSubscription(organizationId);
    if (!data) throw new Error("Failed to check subscription");
    return data;
  },

  getMaterialRequests: async (organizationId) => {
    const res = await ProcurementApi.listMaterialRequests(organizationId);
    return res;
  },

  getPurchaseOrders: async (organizationId) => {
    const res = await ProcurementApi.listPurchaseOrders(organizationId);
    return res;
  },

  getTaskGroupsBySite: async (siteId) => {
    const res = await taskApi.getAllTaskGroupsBySite(siteId);
    return res;
  },
};

// Custom hooks for React Query
const useUserData = (accessToken) => {
  return useQuery({
    queryKey: ["userData", accessToken],
    queryFn: () => apiClient.fetchUserData(accessToken),
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

const useOrganizations = (accessToken, isOwner) => {
  return useQuery({
    queryKey: ["organizations", accessToken],
    queryFn: () => apiClient.fetchOrganizations(accessToken),
    enabled: !!accessToken && !isOwner,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

const useProjectSites = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["projectSites", organizationId],
    queryFn: () => apiClient.fetchProjectSites(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useSiteData = (siteId, accessToken) => {
  return useQuery({
    queryKey: ["siteData", siteId],
    queryFn: () => apiClient.fetchSiteData(siteId, accessToken),
    enabled: !!siteId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useOrgTransactions = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["orgTransactions", organizationId],
    queryFn: () => apiClient.fetchOrgTransactions(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useSubscription = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["subscription", organizationId],
    queryFn: () => apiClient.checkSubscription(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

const useLeads = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["leads", organizationId],
    queryFn: () => apiClient.fetchLeads(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useDesignsByOrg = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["designsByOrg", organizationId],
    queryFn: () => apiClient.fetchDesignsByOrg(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useOrgMembers = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["orgMembers", organizationId],
    queryFn: () => apiClient.fetchOrgMembers(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useFinanceByOrganization = (organizationId, accessToken, period = "30days") => {
  return useQuery({
    queryKey: ["financeByOrganization", organizationId, period],
    queryFn: () => apiClient.fetchFinanceByOrganization(organizationId, period, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useMaterialRequests = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["materialRequests", organizationId],
    queryFn: () => apiClient.getMaterialRequests(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const usePurchaseOrders = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["purchaseOrders", organizationId],
    queryFn: () => apiClient.getPurchaseOrders(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

const useTaskGroupsBySite = (siteId, accessToken) => {
  return useQuery({
    queryKey: ["taskGroups", siteId],
    queryFn: () => apiClient.getTaskGroupsBySite(siteId, accessToken),
    enabled: !!siteId && !!accessToken,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
};

// Utility functions
const calculateOrgSummary = (payment_summary) => {
  const total_paid = Number(payment_summary?.total_paid || 0);
  const total_pending = Number(payment_summary?.total_pending || 0);
  const total_received = Number(payment_summary?.total_received || 0);
  const total_remaining =
    total_received - total_paid < 0 ? 0 : total_received - total_paid;

  return {
    total_paid,
    total_pending,
    total_received,
    total_remaining,
  };
};

const calculateProjectAnalytics = (
  selectedProjectId,
  orgTransactions,
  pendingTransactions,
  selectedSiteData
) => {
  if (!selectedProjectId) {
    return {
      estimated_budget: 0,
      dues: 0,
      received: 0,
      expenses: 0,
    };
  }

  const projectTxns = orgTransactions.filter(
    (txn) => String(txn.site_id) === String(selectedProjectId)
  );

  let estimated_budget = 0;
  if (selectedSiteData?.data?.esrimated_cost) {
    estimated_budget = selectedSiteData.data.esrimated_cost;
  } else if (projectTxns.length > 0) {
    estimated_budget = projectTxns[0].estimated_budget || 0;
  }

  const dues = pendingTransactions
    .filter((txn) => String(txn.site_id) === String(selectedProjectId))
    .reduce((acc, txn) => acc + Number(txn.remaining_payment || 0), 0);

  const received = projectTxns
    .filter((txn) => txn.category === "Received")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  const refund = projectTxns
    .filter((txn) => txn.category === "Refund")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  let expenses = projectTxns
    .filter((txn) => txn.category !== "Received" && txn.category !== "Refund")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  expenses = expenses - refund;
  return { estimated_budget, dues, received, expenses };
};

const extractList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const normalizeLeadStage = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "Lead";
  if (s.includes("quot")) return "Quotation";
  if (s.includes("comp") || s.includes("convert")) return "Completed";
  if (s.includes("active")) return "Active";
  if (s.includes("lead")) return "Lead";
  // keep the dashboard in 4 buckets (Lead/Active/Quotation/Completed)
  return "Lead";
};

const normalizeOrgRole = (member) => {
  const profession = String(member?.user?.profession || "").trim();
  const joinedAs = String(member?.joined_as || "").trim().toLowerCase();

  if (joinedAs === "owner" || profession.toLowerCase() === "owner") return "Owner";

  // Return capitalized profession
  return profession.charAt(0).toUpperCase() + profession.slice(1);
};

const computeWeeklyBucketsFromCumulative = (arr, buckets = 4) => {
  const series = Array.isArray(arr) ? arr.map((x) => Number(x || 0)) : [];
  if (series.length === 0) return Array.from({ length: buckets }, () => 0);

  const deltas = series.map((v, i) => (i === 0 ? v : v - series[i - 1]));
  const bucketSize = Math.ceil(deltas.length / buckets);
  const out = [];
  for (let b = 0; b < buckets; b++) {
    const start = b * bucketSize;
    const end = Math.min(start + bucketSize, deltas.length);
    let sum = 0;
    for (let i = start; i < end; i++) sum += Number(deltas[i] || 0);
    out.push(sum);
  }
  while (out.length < buckets) out.push(0);
  return out.slice(0, buckets);
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const calculatePercentChange = (series) => {
  const values = Array.isArray(series) ? series.map(toFiniteNumber) : [];
  if (values.length < 2) return 0;

  const first = values[0];
  const last = values[values.length - 1];
  if (first === 0) return last === 0 ? 0 : 100;

  return ((last - first) / Math.abs(first)) * 100;
};

const getMetricPercent = (chartData, percentKey, seriesKey) => {
  const explicitPercent = Number(chartData?.[percentKey]);
  if (Number.isFinite(explicitPercent)) return explicitPercent;

  return calculatePercentChange(chartData?.[seriesKey]);
};

const formatPercent = (value) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 1,
  }).format(Math.abs(toFiniteNumber(value)));

const SummaryTrend = ({ percent }) => {
  const trendValue = toFiniteNumber(percent);
  const isDown = trendValue < 0;
  const TrendIcon = isDown ? ArrowDownRight : ArrowUpRight;

  return (
    <span
      className={`${isDown ? "text-red-500" : "text-green-500"} text-xs font-bold flex items-center`}
    >
      <TrendIcon size={12} className="mr-1" /> {formatPercent(trendValue)}%
    </span>
  );
};

const MainContent = ({
  filteredSites,
  onOrganizationClick,
  organizationId,
  onNewProjectClick,
  isOwner,
}) => {
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const queryClient = useQueryClient();
  const accessToken = localStorage.getItem("access");
  const navigate = useNavigate();
  const moduleRoutes = {
    organizationTeam: "/dashboard/organization-team",
    leadManagement: "/dashboard/lead-management",
    design: "/dashboard/design",
    procurement: "/dashboard/procurement",
    finance: "/dashboard/finance",
    vendor: "/dashboard/vendor",
  };

  const handleModuleView = (route) => {
    navigate(route);
  };

  // React Query hooks
  // Removed redundant userDataQuery and organizationsQuery as they were unused
  // const isOwner was only used for useOrganizations enabled check

  const projectSitesQuery = useProjectSites(organizationId, accessToken);
  const siteDataQuery = useSiteData(selectedProjectId, accessToken);
  const orgTransactionsQuery = useOrgTransactions(organizationId, accessToken);
  const subscriptionQuery = useSubscription(organizationId, accessToken);
  const leadsQuery = useLeads(organizationId, accessToken);
  const designsByOrgQuery = useDesignsByOrg(organizationId, accessToken);
  const orgMembersQuery = useOrgMembers(organizationId, accessToken);
  const financeByOrgQuery = useFinanceByOrganization(organizationId, accessToken, "30days");
  const materialRequestsQuery = useMaterialRequests(organizationId, accessToken);
  const purchaseOrdersQuery = usePurchaseOrders(organizationId, accessToken);
  const taskGroupsQuery = useTaskGroupsBySite(selectedProjectId, accessToken);

  // Set initial selected project when sites are loaded
  useEffect(() => {
    if (projectSitesQuery.data?.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projectSitesQuery.data[0].id);
    }
  }, [projectSitesQuery.data, selectedProjectId]);

  // Reset selected project when organization changes
  useEffect(() => {
    if (!organizationId) {
      setSelectedProjectId("");
    }
  }, [organizationId]);

  // Derived state
  // const organizations = organizationsQuery.data?.data || []; // Unused
  const projectSites = projectSitesQuery.data || [];
  const orgTransactions = orgTransactionsQuery.data?.data || [];
  const pendingTransactions =
    orgTransactionsQuery.data?.pending_transactions || [];
  const hasActiveSubscription =
    subscriptionQuery.data?.status &&
    subscriptionQuery.data.data?.active_flag &&
    subscriptionQuery.data.data?.valid_till > new Date().toISOString();

  const orgSummary = calculateOrgSummary(
    orgTransactionsQuery.data?.payment_summary
  );
  const projectAnalytics = calculateProjectAnalytics(
    selectedProjectId,
    orgTransactions,
    pendingTransactions,
    siteDataQuery.data
  );

  // Handle subscription modal close
  const handleSubscriptionClose = () => {
    setSubscriptionOpen(false);
    queryClient.invalidateQueries({
      queryKey: ["subscription", organizationId],
    });
    navigate("/dashboard");
  };

  // Loading states
  const isInitialLoading =
    // userDataQuery.isLoading || // Removed
    // (organizationsQuery.isLoading && !isOwner) || // Removed
    (subscriptionQuery.isLoading && organizationId);

  if (isInitialLoading) {
    // return <ModernB2BLoading />;
    return (
      <LoadingScreen loading={isInitialLoading}>
        <div className="flex items-center justify-center h-screen">
          {/* <ActivityIndicator size="large" color="#0000ff" /> */}
        </div>
      </LoadingScreen>
    )
  }

  // if (subscriptionOpen && isOwner) {
  //   return (
  //     <SubscriptionModal
  //       isOpen={subscriptionOpen}
  //       onClose={handleSubscriptionClose}
  //     />
  //   );
  // }

  // --- API-driven Data for Charts ---
  const leadsList = extractList(leadsQuery.data);
  const leadCounts = leadsList.reduce(
    (acc, lead) => {
      const k = normalizeLeadStage(lead?.status);
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    },
    { Lead: 0, Active: 0, Quotation: 0, Completed: 0 }
  );
  const leadPipelineData = [
    { name: "Lead", value: leadCounts.Lead || 0, fill: "#C4B5FD" },
    { name: "Active", value: leadCounts.Active || 0, fill: "#8B5CF6" },
    { name: "Quotation", value: leadCounts.Quotation || 0, fill: "#F59E0B" },
    { name: "Completed", value: leadCounts.Completed || 0, fill: "#10B981" },
  ];

  const designsList = extractList(designsByOrgQuery.data);
  const designCounts = designsList.reduce(
    (acc, d) => {
      const s = String(d?.status || "").trim();
      if (s === "Approved") acc.Approved += 1;
      else if (s === "Locked") acc.Locked += 1;
      else acc.Pending += 1; // Draft/Under Review/Unknown -> Pending bucket
      return acc;
    },
    { Approved: 0, Pending: 0, Locked: 0 }
  );
  const designStatusData = [
    { name: "Approved", value: designCounts.Approved, fill: "#10B981" },
    { name: "Pending", value: designCounts.Pending, fill: "#F59E0B" },
    { name: "Locked", value: designCounts.Locked, fill: "#4C1D95" },
  ];

  // Task Stats
  const taskGroups = extractList(taskGroupsQuery.data);
  const taskStats = taskGroups.reduce(
    (acc, tg) => {
      const summary = tg?.task_group_summary || {};
      acc.total += Number(summary.total_tasks || 0);
      acc.completed += Number(summary.completed_tasks || 0);
      return acc;
    },
    { total: 0, completed: 0 }
  );

  // Total Designs for Selected Site
  console.log("DESIGNS", designsList);
  const siteDesignsCount = designsList.filter(d => String(d.site) === String(selectedProjectId)).length;

  // Total Purchase Orders for Selected Site
  const poList = extractList(purchaseOrdersQuery.data);
  const sitePOCount = poList.filter(po => String(po.site) === String(selectedProjectId)).length;

  const mrList = extractList(materialRequestsQuery.data);
  const last6Months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    last6Months.push({
      name: d.toLocaleString("default", { month: "short" }),
      monthIndex: d.getMonth(),
      year: d.getFullYear(),
      requests: 0,
      completed: 0,
    });
  }

  mrList.forEach((mr) => {
    const createdOn = mr?.created_on ? new Date(mr.created_on) : null;
    if (createdOn) {
      const m = createdOn.getMonth();
      const y = createdOn.getFullYear();
      const bucket = last6Months.find((b) => b.monthIndex === m && b.year === y);
      if (bucket) {
        bucket.requests += 1;
        if (mr.status === "COMPLETED") {
          bucket.completed += 1;
        }
      }
    }
  });
  const procurementTrendData = last6Months;

  const financePayload = financeByOrgQuery.data || {};
  const financeChartData =
    financePayload?.payment_summary?.chart_data ||
    financePayload?.data?.payment_summary?.chart_data ||
    orgTransactionsQuery.data?.payment_summary?.chart_data ||
    {};
  const summaryMetricPercents = {
    expenses: getMetricPercent(financeChartData, "expenses_percent", "expenses"),
    dues: getMetricPercent(financeChartData, "dues_percent", "dues"),
    received: getMetricPercent(financeChartData, "received_percent", "received"),
    balance: getMetricPercent(financeChartData, "remaining_percent", "remaining"),
  };
  const expensesBuckets = computeWeeklyBucketsFromCumulative(financeChartData?.expenses, 4);
  const receivedBuckets = computeWeeklyBucketsFromCumulative(financeChartData?.received, 4);
  const cashFlowData = [
    { name: "Wk 1", Inflow: receivedBuckets[0] || 0, Outflow: expensesBuckets[0] || 0 },
    { name: "Wk 2", Inflow: receivedBuckets[1] || 0, Outflow: expensesBuckets[1] || 0 },
    { name: "Wk 3", Inflow: receivedBuckets[2] || 0, Outflow: expensesBuckets[2] || 0 },
    { name: "Wk 4", Inflow: receivedBuckets[3] || 0, Outflow: expensesBuckets[3] || 0 },
  ];

  const financePendingTransactions =
    financePayload?.pending_transactions ||
    financePayload?.data?.pending_transactions ||
    [];
  const pendingByVendor = (Array.isArray(financePendingTransactions) ? financePendingTransactions : []).reduce(
    (acc, txn) => {
      const name = String(txn?.vendor_name || "Unknown").trim() || "Unknown";
      const amt = Number(txn?.remaining_payment || 0);
      acc[name] = (acc[name] || 0) + amt;
      return acc;
    },
    {}
  );
  const topPendingDuesData = Object.entries(pendingByVendor)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
    .slice(0, 5);

  const membersList = extractList(orgMembersQuery.data);
  const professionColors = {
    Engineers: "#8B5CF6",
    Contractors: "#F59E0B",
    Admins: "#4C1D95",
    Finance: "#10B981",
    // Fallback colors for unknown professions
    Other: "#64748B"
  };
  const chartPalette = ["#8B5CF6", "#F59E0B", "#4C1D95", "#10B981", "#EC4899", "#3B82F6", "#06B6D4", "#84CC16"];

  const roleCounts = membersList.reduce(
    (acc, m) => {
      const role = normalizeOrgRole(m);
      if (role === "Owner") return acc;
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    },
    {}
  );
  const teamDistributionData = Object.entries(roleCounts).map(([name, value], index) => ({
    name,
    value,
    fill: professionColors[name] || chartPalette[index % chartPalette.length]
  }));

  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const newThisMonthMembers = membersList
    .filter((m) => {
      const created = m?.created_on ? new Date(m.created_on) : null;
      if (!created || isNaN(created.getTime())) return false;
      return created.getMonth() === month && created.getFullYear() === year;
    })
    .sort((a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime())
    .slice(0, 2);

  const recentActivities = [
    {
      title: "Scope Approved",
      project: "Green Valley",
      time: "2h ago",
      color: "bg-green-500",
    },
    {
      title: "Quotation Sent",
      project: "Tech Park",
      time: "4h ago",
      color: "bg-purple-500",
    },
    {
      title: "Design Locked",
      project: "Site Mutation",
      time: "6h ago",
      color: "bg-indigo-900",
    },
    {
      title: "Procurement Created",
      project: "Skyline",
      time: "1d ago",
      color: "bg-orange-500",
    },
    {
      title: "Payment Released",
      project: "Metro Heights",
      time: "1d ago",
      color: "bg-green-500",
    },
  ];

  const projectAnalyticsChartData = [
    { name: "Budget", value: projectAnalytics.estimated_budget, fill: "#10B981" },
    { name: "Expenses", value: projectAnalytics.expenses, fill: "#EF4444" },
    { name: "Dues", value: projectAnalytics.dues, fill: "#F59E0B" },
    { name: "Received", value: projectAnalytics.received, fill: "#8B5CF6" },
  ];

  // Filter out zero values for cleaner chart
  const activeProjectAnalyticsData = projectAnalyticsChartData.filter(d => d.value > 0);


  if (!hasActiveSubscription && isOwner) {
    return (
      <SubscriptionModal
        isOpen={true}
        onClose={handleSubscriptionClose}
      />
    );
  }

  return (
    <div className="px-10 pt-8 pb-4" style={{
      scrollbarWidth: "none",
    }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Global Dashboard</h1>
          {/* <p className="text-slate-500 text-sm">
            Unified overview of finances, projects, design, and communication
          </p> */}
        </div>
        {/* <div className="flex gap-3">
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 flex items-center gap-2 hover:bg-slate-50">
            Last 30 days <ChevronDown size={16} />
          </button>
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 flex items-center gap-2 hover:bg-slate-50">
            All Branches <ChevronDown size={16} />
          </button>
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 flex items-center gap-2 hover:bg-slate-50">
            All Projects <ChevronDown size={16} />
          </button>
        </div> */}
      </div>

      {/* Company Finances */}
      <div className="mb-6">
        {/* <div className="flex items-center gap-2 mb-4">
          <RiMoneyRupeeCircleFill size={24} color="#3B0069" />
          <h2 className="text-lg font-semibold text-slate-800">
            Company Finances
          </h2>
        </div> */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Expenses */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <span className="text-slate-500 text-sm font-medium">Expenses</span>
              <SummaryTrend percent={summaryMetricPercents.expenses} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              ₹{orgSummary.total_paid.toLocaleString("en-IN")}
            </h3>
            {/* Simple Sparkline Placeholder */}
            <div className="h-8 w-full bg-gradient-to-t from-red-50 to-transparent absolute bottom-0 left-0 right-0 opacity-50">
              <svg
                viewBox="0 0 100 20"
                className="w-full h-full text-sm-primary fill-current"
                preserveAspectRatio="none"
              >
                <path d="M0 20 L0 10 Q25 5 50 15 T100 10 L100 20 Z" />
              </svg>
            </div>
          </div>

          {/* Dues */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <span className="text-slate-500 text-sm font-medium">Dues</span>
              <SummaryTrend percent={summaryMetricPercents.dues} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              ₹{orgSummary.total_pending.toLocaleString("en-IN")}
            </h3>
            <div className="h-8 w-full bg-gradient-to-t from-orange-50 to-transparent absolute bottom-0 left-0 right-0 opacity-50">
              <svg
                viewBox="0 0 100 20"
                className="w-full h-full text-sm-accent fill-current"
                preserveAspectRatio="none"
              >
                <path d="M0 20 L0 12 Q25 18 50 10 T100 15 L100 20 Z" />
              </svg>
            </div>
          </div>

          {/* Received */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <span className="text-slate-500 text-sm font-medium">Received</span>
              <SummaryTrend percent={summaryMetricPercents.received} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              ₹{orgSummary.total_received.toLocaleString("en-IN")}
            </h3>
            <div className="h-1 w-full bg-green-500 absolute bottom-0 left-0"></div>
          </div>

          {/* Balance */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <span className="text-slate-500 text-sm font-medium">Balance</span>
              <SummaryTrend percent={summaryMetricPercents.balance} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              ₹{orgSummary.total_remaining.toLocaleString("en-IN")}
            </h3>
            <div className="h-8 w-full bg-gradient-to-t from-purple-50 to-transparent absolute bottom-0 left-0 right-0 opacity-50">
              <svg
                viewBox="0 0 100 20"
                className="w-full h-full text-sm-secondary fill-current"
                preserveAspectRatio="none"
              >
                <path d="M0 20 L0 5 L100 15 L100 20 Z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Project Analytics */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-slate-800">
              Project Analytics
            </h2>
            <select
              className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block p-2"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              {projectSites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.site_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Donut Chart */}
            <div className="w-48 h-48 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={activeProjectAnalyticsData.length > 0 ? activeProjectAnalyticsData : [{ name: 'No Data', value: 1 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {activeProjectAnalyticsData.length > 0 ? activeProjectAnalyticsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    )) : <Cell fill="#e2e8f0" />}
                    <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Stats Grid */}
            <div className="flex-1 w-full">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs text-slate-500 uppercase font-semibold">
                      Budget
                    </span>
                  </div>
                  <p className="text-lg font-bold text-slate-800">
                    ₹{projectAnalytics.estimated_budget.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-xs text-slate-500 uppercase font-semibold">
                      Expenses
                    </span>
                  </div>
                  <p className="text-lg font-bold text-slate-800">
                    ₹{projectAnalytics.expenses.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-orange-500">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-xs text-slate-500 uppercase font-semibold">
                      Dues
                    </span>
                  </div>
                  <p className="text-lg font-bold text-slate-800">
                    ₹{projectAnalytics.dues.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border-l-4 border-purple-500">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-xs text-slate-500 uppercase font-semibold">
                      Received
                    </span>
                  </div>
                  <p className="text-lg font-bold text-slate-800">
                    ₹{projectAnalytics.received.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {/* Budget Progress */}
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 font-medium">Budget Used</span>
                  <span className="text-slate-900 font-bold">
                    {projectAnalytics.estimated_budget > 0
                      ? Math.round(
                        (projectAnalytics.expenses /
                          projectAnalytics.estimated_budget) *
                        100
                      )
                      : 0}
                    %
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-purple-900 h-2 rounded-full"
                    style={{
                      width: `${projectAnalytics.estimated_budget > 0
                        ? Math.min(
                          (projectAnalytics.expenses /
                            projectAnalytics.estimated_budget) *
                          100,
                          100
                        )
                        : 0
                        }%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Chips */}
          <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-100">
            <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
              <CheckCircle size={12} /> {taskGroupsQuery.isLoading ? "Loading..." : `Tasks Completed: ${taskStats.completed}/${taskStats.total}`}
            </span>
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
              <FileText size={12} /> {designsByOrgQuery.isLoading ? "Loading..." : `Total Designs: ${siteDesignsCount}`}
            </span>
            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium flex items-center gap-1">
              <Briefcase size={12} /> {purchaseOrdersQuery.isLoading ? "Loading..." : `Procurement Orders: ${sitePOCount}`}
            </span>
            {/* <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium flex items-center gap-1">
              <DollarSign size={12} /> Contractor Payments: ₹4.2L
            </span> */}
          </div>
        </div>

        {/* Organization */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-slate-800">
              Organization
            </h2>
            <button
              type="button"
              onClick={() => handleModuleView(moduleRoutes.organizationTeam)}
              className="text-sm text-slate-500 hover:text-purple-700 flex items-center gap-1"
            >
              View <ArrowUpRight size={14} />
            </button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-700">
              <Users size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800">
                {membersList.length}
              </h3>
              <p className="text-slate-500 text-sm">Total Members</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {teamDistributionData.map((item, idx) => (
              <div key={idx} className="bg-slate-50 py-2 px-3 rounded-lg text-center" style={{ backgroundColor: `${item.fill}10` }}>
                <span className="font-medium text-sm" style={{ color: item.fill }}>
                  {item.value} {item.name}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs text-slate-400 font-medium mb-3 uppercase flex items-center gap-1">
              <Users size={12} /> New this month
            </p>
            <div className="space-y-3">
              {newThisMonthMembers.length === 0 ? (
                <div className="text-xs text-slate-500">No new members this month.</div>
              ) : (
                newThisMonthMembers.map((m) => {
                  const fn = m?.user?.first_name || "";
                  const ln = m?.user?.last_name || "";
                  const fullName = `${fn} ${ln}`.trim() || m?.user?.email || "Member";
                  const role = normalizeOrgRole(m);
                  return (
                    <div key={m?.id || fullName} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex border border-slate-200 overflow-hidden items-center justify-center bg-slate-200">
                          {m?.user?.userpic ? (
                            <img src={m?.user?.userpic} alt="" className="w-full h-full object-cover rounded-full" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-sm-primary">
                              <span className="text-xs text-white">
                                {fn.charAt(0).toUpperCase() + ln.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-medium text-slate-700">
                          {fullName}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">{role}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* My Projects */}
      {/* <div className="mb-6">
        <MyProjects
          filteredSites={filteredSites}
          organizationId={organizationId}
          onNewProjectClick={onNewProjectClick}
          onBack={() => {
            window.location.reload();
          }}
        />
      </div> */}

      {/* Feature Analytics */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={20} className="text-purple-900" />
          <h2 className="text-lg font-semibold text-slate-800">
            Feature Analytics
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Lead Pipeline */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Lead Pipeline
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.leadManagement)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={leadPipelineData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={60}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {leadPipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Design Status */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Design Status
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.design)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="flex items-center">
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={designStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={55}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {designStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 pl-4 space-y-2">
                {designStatusData.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      ></div>
                      <span className="text-xs text-slate-600">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-800">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Procurement Trend */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Procurement Trend
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.procurement)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={procurementTrendData}>
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="requests"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center gap-1">
                <div className="w-2 h-1 bg-orange-500 rounded-full"></div>
                <span className="text-[10px] text-slate-500">Requests</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-1 bg-green-500 rounded-full"></div>
                <span className="text-[10px] text-slate-500">Completed</span>
              </div>
            </div>
          </div>

          {/* Cash Flow */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Cash Flow
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.finance)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashFlowData} barGap={2}>
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                  <Bar
                    dataKey="Inflow"
                    fill="#10B981"
                    radius={[2, 2, 0, 0]}
                    barSize={10}
                  />
                  <Bar
                    dataKey="Outflow"
                    fill="#EF4444"
                    radius={[2, 2, 0, 0]}
                    barSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center gap-1">
                <div className="w-2 h-1 bg-green-500 rounded-full"></div>
                <span className="text-[10px] text-slate-500">Inflow</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-1 bg-red-500 rounded-full"></div>
                <span className="text-[10px] text-slate-500">Outflow</span>
              </div>
            </div>
          </div>

          {/* Top Pending Dues */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Top Pending Vendors Dues
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.vendor)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={topPendingDuesData}
                  margin={{ top: 5, right: 20, bottom: 5 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={70}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                  <Bar
                    dataKey="value"
                    fill="#8B5CF6"
                    radius={[0, 4, 4, 0]}
                    barSize={15}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Team Distribution */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Team Distribution
              </h3>
              <button
                type="button"
                onClick={() => handleModuleView(moduleRoutes.organizationTeam)}
                className="text-xs text-slate-500 flex items-center gap-1"
              >
                View <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="flex items-center">
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={teamDistributionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={55}
                      dataKey="value"
                    >
                      {teamDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 pl-4 space-y-2">
                {teamDistributionData.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      ></div>
                      <span className="text-xs text-slate-600">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-800">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      {/* <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp size={20} className="text-purple-900" />
            <h2 className="text-lg font-semibold text-slate-800">
              Recent Activities
            </h2>
          </div>
          <button className="text-sm text-slate-500 hover:text-purple-700">
            View All
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {recentActivities.map((activity, index) => (
            <div
              key={index}
              className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center gap-3"
            >
              <div
                className={`w-2 h-2 rounded-full ${activity.color} flex-shrink-0`}
              ></div>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  {activity.title}
                </h4>
                <p className="text-xs text-slate-500">
                  {activity.project} • {activity.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default MainContent;
